import React, { useState } from 'react';
import { ModalBase } from './ModalBase';

interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, children, isOpen, onClick }) => (
    <div className="border-b border-gray-200 py-4">
        <button
            className="flex justify-between items-center w-full text-left font-semibold text-lg text-gray-800"
            onClick={onClick}
            aria-expanded={isOpen}
        >
            <span>{title}</span>
            <span className={`material-icons-outlined transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                expand_more
            </span>
        </button>
        <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[10000px] mt-4' : 'max-h-0'}`}>
            <div className="prose prose-sm max-w-none text-gray-600">
                {children}
            </div>
        </div>
    </div>
);


const normativas = [
  {
    id: 'rgpd',
    title: 'Reglamento General de Protección de Datos (RGPD)',
    content: (
      <>
        <p>
          El RGPD (Reglamento (UE) 2016/679) es una normativa europea que regula el tratamiento de los datos personales. Establece los derechos de los ciudadanos sobre sus datos, como el derecho al acceso, rectificación, supresión ("derecho al olvido") y portabilidad. Afecta a cualquier organización que trate datos de ciudadanos de la UE, imponiendo estrictas obligaciones de seguridad, transparencia y accountability. Su incumplimiento puede acarrear multas de hasta 20 millones de euros o el 4% de la facturación anual global.
        </p>
        <a 
          href="https://blog.multas.org/2025/08/05/resoluciones-de-autoridades-de-proteccion-de-datos-sobre-cesiones-ilegales-y-principios-del-rgpd/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline font-medium mt-2 block"
        >
          Resoluciones de Autoridades de Protección de Datos sobre Cesiones Ilegales y Principios del RGPD
        </a>
      </>
    )
  },
  {
    id: 'trafico',
    title: 'Ley de Tráfico, Circulación de Vehículos a Motor y Seguridad Vial',
    content: <p>Esta ley establece el marco normativo para la circulación en las vías públicas de España. Regula desde los límites de velocidad, las tasas de alcoholemia permitidas, el uso de sistemas de retención infantil, hasta el régimen sancionador y el sistema de puntos del permiso de conducir. Ha sido actualizada recientemente para incluir normativas sobre Zonas de Bajas Emisiones (ZBE) y el uso de Vehículos de Movilidad Personal (VMP) como los patinetes eléctricos.</p>
  },
  {
    id: 'zbe',
    title: 'Normativa sobre Zonas de Bajas Emisiones (ZBE)',
    content: (
      <>
        <p>Reguladas por la Ley de Cambio Climático y Transición Energética, las ZBE son áreas delimitadas en ciudades de más de 50.000 habitantes donde se restringe el acceso, la circulación y el estacionamiento de los vehículos más contaminantes. El objetivo es mejorar la calidad del aire. El acceso se controla mediante las etiquetas medioambientales de la DGT (0 Emisiones, ECO, C, B). Circular por una ZBE sin el distintivo adecuado o sin autorización conlleva sanciones económicas.</p>
        <a 
          href="https://blog.multas.org/archivos/73"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline font-medium mt-2 block"
        >
          Las Sentencias que Tumbaron la ZBE del Ayuntamiento de Barcelona: Victoria Legal Histórica de Nuestro Servicio Jurídico
        </a>
      </>
    )
  },
  {
    id: 'consumidores',
    title: 'Directiva Europea de Derechos de los Consumidores',
    content: <p>La Directiva 2011/83/UE y sus posteriores modificaciones refuerzan los derechos de los consumidores en toda la UE, especialmente en compras online y a distancia. Establece un período de desistimiento de 14 días sin necesidad de justificación, regula la información precontractual que debe recibir el consumidor, y prohíbe las casillas pre-marcadas para servicios adicionales. También establece normas sobre la entrega de bienes y la transferencia del riesgo.</p>
  },
];


interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NormativasVariasModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
    const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);

    const handleToggle = (id: string) => {
        setOpenAccordionId(openAccordionId === id ? null : id);
    };

    return (
        <ModalBase isOpen={isOpen} onClose={onClose} title="Normativas Varias de Interés" containerClassName="max-w-4xl">
            <p className="mb-6 text-gray-700">
                Aquí encontrará un resumen de algunas de las normativas más relevantes que afectan a los ciudadanos y conductores en el ámbito europeo y español. Esta información es orientativa y no sustituye el asesoramiento legal profesional.
            </p>
            <div className="space-y-2">
                {normativas.map((item) => (
                    <AccordionItem
                        key={item.id}
                        title={item.title}
                        isOpen={openAccordionId === item.id}
                        onClick={() => handleToggle(item.id)}
                    >
                       {item.content}
                    </AccordionItem>
                ))}
            </div>
             <div className="mt-8 text-center text-sm text-gray-500">
                <p>Para un análisis detallado de su caso a la luz de estas u otras normativas, no dude en contactar con nuestro equipo de expertos.</p>
            </div>
        </ModalBase>
    );
};